import React, { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";

import createOrder from "@/api/requests/createOrder";
import publicConfig from "@/config/public";
import { Action } from "@/constants/events";
import { CartItem } from "@/custom-types/Cart";
import useDomainCountryCode from "@/hooks/useDomainCountryCode";
import { useEventTracker } from "@/hooks/useEventTracker";
import { updateCart } from "@/redux/action-creators/updateCart";
import { useDispatch, useSelector } from "@/redux/hooks";
import {
  getCart,
  getCartContext,
  getCartSessionId,
} from "@/redux/selectors/cart";
import { getCartDispensaryData } from "@/redux/selectors/cartDispensary";
import { getOrderGuestToken, getOrderId } from "@/redux/selectors/order";
import { getFullUser } from "@/redux/selectors/user";
import { saveOrderSuccess } from "@/redux/slices/order";

import JaneFrame from "./JaneFrame";
import {
  getCartItemFromJaneProductId,
  getJaneProductsFromCartItems,
  getOutOfStockCartItems,
} from "./utils";

// https://leafly.atlassian.net/wiki/spaces/ENG/pages/2173304854/Jane+Checkout for more details around testing

type Props = {
  cartItems: CartItem[];
};

type JaneUser = {
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  phone?: string;
  email?: string;
};

type JaneCheckoutPayload = {
  name: "checkout";
  properties: {
    uuid: string;
    customerBirthDate: string;
    customerEmail: string;
    customerFirstName: string;
    customerLastName: string;
    customerPhone: string;
    deliveryAddress: {
      street: string;
      street2: string;
      city: string;
      lat: number;
      lng: number;
      zipcode: string;
      state_code: string;
    };
    storeTax: number;
    salesTax: number;
    estimatedTotal: number;
    preDiscountSubtotal: number;
    discountTotal: number;
    reservationMode: "pickup" | "delivery";
  };
};

const JaneCheckout = ({ cartItems }: Props) => {
  const { jane } = publicConfig;

  const dispatch = useDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [loading, setLoading] = useState(true);

  const trackingApi = useEventTracker();
  const { publishEvent } = trackingApi;
  const cartContext = useSelector(getCartContext);
  const dispensary = useSelector(getCartDispensaryData);
  const user = useSelector(getFullUser);
  const cart = useSelector(getCart);
  const sessionId = useSelector(getCartSessionId);
  const orderId = useSelector(getOrderId);
  const guestToken = useSelector(getOrderGuestToken);
  const domainCountryCode = useDomainCountryCode();

  const cartId = cart?.data?.id;

  const userNameArray = user?.name?.trim().split(" ") || [];
  const userFirstName = userNameArray[0];
  const userLastName = userNameArray[userNameArray.length - 1];
  const userDateOfBirth = user?.dateOfBirth;
  const userPhone = user?.phoneNumber;
  const userEmail = user?.email;

  let janeUser: JaneUser;
  if (user.id) {
    janeUser = {
      birthDate: userDateOfBirth,
      email: userEmail,
      firstName: userFirstName,
      lastName: userLastName,
      phone: userPhone,
    };
  }

  useEffect(() => {
    publishEvent({
      action: Action.impression,
      category: "Bag",
      label: "Jane",
      nonInteractionEvent: true,
      // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
      userId: user?.id,
    });
  }, []);

  useEffect(() => {
    if (orderId) {
      const orderStatusSearchParams = new URLSearchParams(
        searchParams?.toString(),
      );
      if (guestToken) {
        orderStatusSearchParams.set("guestToken", guestToken);
      }
      const orderQueryString = orderStatusSearchParams.toString();

      router.replace(
        `/order-status/${orderId}${orderQueryString ? `?${orderQueryString}` : ""}`,
      );
    }
  }, [orderId]);

  const products = getJaneProductsFromCartItems(cartItems);
  const onPostMessage = (event: MessageEvent) => {
    const janeFrame = document.getElementById("jane-menu") as HTMLIFrameElement;

    if (
      !(
        event &&
        event.data &&
        event.data.messageType &&
        ["loadingEvent", "analyticsEvent"].includes(event.data.messageType)
      )
    ) {
      return;
    }

    const payload = event.data.payload;

    switch (payload.name) {
      case "checkout": {
        const orderInfo: JaneCheckoutPayload["properties"] = {
          // At times jane might fail to send us every field that is required
          // by res-api. This ensures we won't fail api validation since we are
          // not the source of truth for the data, but it is important we have a record
          // of all orders even with potentially incomplete data.
          customerBirthDate: "1901-01-01",
          customerEmail: "IHJ@leafly.com",
          customerFirstName: "Jane",
          customerLastName: "Order",
          customerPhone: "5553617232",
          deliveryAddress: {},

          reservationMode: "pickup",
          ...event.data.payload.properties,
        };

        const fulfillmentValues =
          orderInfo.reservationMode === "delivery"
            ? {
                address1: orderInfo.deliveryAddress.street,
                address2: orderInfo.deliveryAddress.street2,
                addressType: "residential",
                city: orderInfo.deliveryAddress.city,
                coordinates: {
                  lat: orderInfo.deliveryAddress.lat,
                  lng: orderInfo.deliveryAddress.lng,
                },
                deliveryInstructions: "",
                fulfillmentMechanism: "delivery",
                paymentPreference: "cash",
                postalCode: orderInfo.deliveryAddress.zipcode,
                state: orderInfo.deliveryAddress.state_code,
              }
            : {
                fulfillmentMechanism: "pickup",
              };

        const _toPennies = (dollars: number) => 100 * (dollars ?? 0);

        const saveReservationBody = {
          reservation: {
            cartId,
            dateOfBirth: orderInfo.customerEmail,
            emailAddress: orderInfo.customerEmail,
            embedVersion: null,
            externalOrderId: orderInfo.uuid,
            firstName: orderInfo.customerFirstName,
            lastName: orderInfo.customerLastName,
            menuType: null, //TODO: update this once Res API has a jane menu type
            phoneNumber: orderInfo.customerPhone,
            saveUserInfo: false,
            subtotal: _toPennies(orderInfo.preDiscountSubtotal),
            tax:
              _toPennies(orderInfo.storeTax) + _toPennies(orderInfo.salesTax),
            total:
              _toPennies(orderInfo.preDiscountSubtotal) -
              _toPennies(orderInfo.discountTotal),
            totalDiscountsCents: _toPennies(orderInfo.discountTotal),
            ...fulfillmentValues,
          },
        };
        setLoading(true);
        createOrder(sessionId, saveReservationBody, domainCountryCode)
          .then((response) => {
            publishEvent({
              action: Action.Click,
              category: "Checkout Confirmation Page",
              label: "Send Order",
              // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
              orderId: response.data.id,
              subtotal: _toPennies(orderInfo.preDiscountSubtotal),
            });
            dispatch(saveOrderSuccess(response.data));
          })
          .catch(() => {
            publishEvent({
              action: Action.Click,
              // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
              bagId: cartId,
              category: "Checkout Confirmation Page",
              errorMessage: "Could not save reservation",
              label: "Send Order Error",
            });
          });
        break;
      }
      case "cartPageLoad": {
        const {
          properties: { cartProducts },
        } = payload;
        const outOfStockItems = getOutOfStockCartItems(
          cartItems,
          products,
          cartProducts,
        );
        console.info(cartProducts);
        outOfStockItems.forEach((cartItem) => {
          console.info(cartItem);
          updateCart(
            dispatch,
            trackingApi,
            { ...cartItem, quantity: 0 },
            "Removed Item",
            sessionId,
            true,
            cartContext,
            domainCountryCode,
          );
        });
        setLoading(false);
        break;
      }
      case "cartCreateOrUpdate": {
        setLoading(false);
        break;
      }
      case "cartItemChangeCount": {
        const {
          properties: { productId, count },
        } = payload;

        const cartItem = getCartItemFromJaneProductId(
          productId,
          cartItems,
          products,
        );
        updateCart(
          dispatch,
          trackingApi,
          { ...cartItem, quantity: count },
          "Changed Quantity",
          sessionId,
          true,
          cartContext,
          domainCountryCode,
        );
        break;
      }
      case "cartItemRemoval": {
        const {
          properties: { productId },
        } = payload;
        const cartItem = getCartItemFromJaneProductId(
          productId,
          cartItems,
          products,
        );
        updateCart(
          dispatch,
          trackingApi,
          { ...cartItem, quantity: 0 },
          "Removed Item",
          sessionId,
          true,
          cartContext,
          domainCountryCode,
        );
        break;
      }
      case "headlessAppLoaded": {
        const redirectUrl = `${window.location.origin}/dispensary-info/${dispensary?.slug}`;
        const data = {
          messageType: "buildCart",
          payload: {
            headlessCheckoutPartnerId: jane.checkoutId,
            headlessPartnerName: "Leafly",
            options: {
              disableAuthFeatures: true,
              disableLoadingSpinner: true,
              disableMarketingCheckoutAgreements: true,
              disablePrepayment: true,
              disableWeightSelection: true,
              font: {
                fontFamily: "Public Sans",
                url: "https://public.leafly.com/fonts/public-sans/public-sans-v4-latin-regular.woff2",
              },
              redirectUrl,
              theme: {
                ctaTextColor: "#fff",
                navigationColor: "#ffcd00",
                themeColor: "#017c6b",
              },
            },
            products,
            /**
             * This is order blocking for IHJ retailers that have only one
             * reservation mode enabled. Commenting this out until a better
             * solution is found for sending fulfillment preference.
             * Slack thread: https://leafly.slack.com/archives/C020FEVTM9N/p1711124619430229
             */
            // reservationMode: fulfillmentPreference,
            storeId: dispensary?.janeStoreId,
            user: janeUser,
          },
        };
        if (janeFrame?.contentWindow) {
          janeFrame.contentWindow.postMessage(data, "*");
        }
        break;
      }
      case "reportCartDisabled": {
        if (
          payload &&
          payload.properties &&
          payload.properties.isCheckoutDisabled
        ) {
          publishEvent({
            action: "Jane Checkout",
            // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
            bagId: cartId,
            category: "Checkout Confirmation Page",
            errorMessage: "Could not save reservation",
            label: "Report Cart Disabled",
          });
        }
        break;
      }
      case "reportCheckoutDisabled": {
        if (
          payload &&
          payload.properties &&
          payload.properties.isCheckoutDisabled
        ) {
          publishEvent({
            action: "Jane Checkout",
            // @ts-ignore -- TODO: Fix publishEvent types (https://leafly.atlassian.net/browse/SHH-1133)
            bagId: cartId,
            category: "Checkout Confirmation Page",
            errorMessage: "Could not save reservation",
            label: "Report Checkout Disabled",
          });
        }
        break;
      }
      default: {
        break;
      }
    }
  };
  return (
    <JaneFrame
      onPostMessage={onPostMessage}
      janeCheckoutUrl={jane.checkoutUrl}
      loading={loading}
    />
  );
};

export { JaneCheckout };
